/**
 * Caption preset metadata for picker UIs (create-video Advanced Settings,
 * Video Editor right panel). The actual rendering specs live in the
 * Remotion compositions (Composition.tsx and VideoEditorComposition.tsx)
 * keyed by the same `id`.
 */

export type CaptionPresetId =
  | 'default'
  | 'viral'
  | 'hormozi'
  | 'wrap'
  | 'faceless'
  | 'minimal-white';

export interface CaptionPresetOption {
  id: CaptionPresetId;
  label: string;
  description: string;
  /** Sample rendered styles for the preview chip. */
  preview: {
    fontFamily: string;
    fontWeight: number;
    uppercase: boolean;
    restColor: string;
    highlightColor: string;
    highlightBackground?: string;
    textShadow?: string;
  };
}

const BEVEL_PREVIEW =
  '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000';
const OUTLINE_PREVIEW =
  '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, -1px 0 0 #000, 1px 0 0 #000, 0 -1px 0 #000, 0 1px 0 #000';
const SOFT_SHADOW = '0 2px 6px rgba(0,0,0,0.55)';

export const CAPTION_PRESET_OPTIONS: CaptionPresetOption[] = [
  {
    id: 'viral',
    label: 'Viral',
    description: 'Lexend yellow bevel with bouncy entrance. TikTok-ready.',
    preview: {
      fontFamily: 'Lexend, sans-serif',
      fontWeight: 700,
      uppercase: true,
      restColor: '#FFFFFF',
      highlightColor: '#fcb900',
      textShadow: BEVEL_PREVIEW,
    },
  },
  {
    id: 'hormozi',
    label: 'Hormozi',
    description: 'Big white text, thick outline, yellow word highlight.',
    preview: {
      fontFamily: 'Lexend, sans-serif',
      fontWeight: 700,
      uppercase: true,
      restColor: '#FFFFFF',
      highlightColor: '#fcd34d',
      textShadow: OUTLINE_PREVIEW,
    },
  },
  {
    id: 'wrap',
    label: 'Wrap',
    description: 'Current word in a filled yellow box. Bold and clean.',
    preview: {
      fontFamily: 'Lexend, sans-serif',
      fontWeight: 700,
      uppercase: true,
      restColor: '#FFFFFF',
      highlightColor: '#000000',
      highlightBackground: '#fcd34d',
      textShadow: SOFT_SHADOW,
    },
  },
  {
    id: 'faceless',
    label: 'Faceless',
    description: 'Plain bold white with thick outline. No color highlight.',
    preview: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 800,
      uppercase: false,
      restColor: '#FFFFFF',
      highlightColor: '#FFFFFF',
      textShadow: OUTLINE_PREVIEW,
    },
  },
  {
    id: 'default',
    label: 'Classic',
    description: 'Inter white with green word highlight and soft shadow.',
    preview: {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 800,
      uppercase: false,
      restColor: '#FFFFFF',
      highlightColor: '#39E508',
      textShadow: SOFT_SHADOW,
    },
  },
  {
    id: 'minimal-white',
    label: 'Minimal',
    description: 'Montserrat white. Subtle — no highlight color change.',
    preview: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 600,
      uppercase: false,
      restColor: '#FFFFFF',
      highlightColor: '#FFFFFF',
      textShadow: SOFT_SHADOW,
    },
  },
];

export const DEFAULT_CAPTION_PRESET: CaptionPresetId = 'viral';
